import * as sdk from 'microsoft-cognitiveservices-speech-sdk';

const SPEECH_ENDPOINT = 'https://text-to-speech-mlpcare-resource.cognitiveservices.azure.com';
const SPEECH_VOICE_NAME = 'tr-TR-Elif:MAI-Voice-2';

// Tarayicinin otomatik oynatma (autoplay) kisitlamasini kullanicinin
// gercek bir tiklamasi sirasinda "acmak" icin. Sessiz/kisa bir oynatma
// denemesi, sonraki programatik play() cagrilarinin engellenme riskini azaltir.
export const unlockAudioPlayback = () => {
    try {
        const audio = new Audio();
        audio.muted = true;
        const playPromise = audio.play();
        if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {
                // yoksayilir, sadece deneme
            });
        }
    } catch {
        // yoksayilir
    }
};

// Konusma hizi, Azure SSML prosody "rate" degeri olarak kullanilir.
// Ornekler: "+20%" (yuzde 20 daha hizli), "1.2" (1.2x), "fast", "slow".
const SPEECH_RATE = '+20%';

const escapeSsml = (text: string) =>
    text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');

// TTS unvan kisaltmalarini ("Uzm.Dr." gibi) kotu/yanlis okuyor, bu yuzden
// seslendirmeden once acilimlarina genisletiliyor. Sira onemli: bir kisaltma
// baska birinin alt-string'i olabildigi icin (ör. "Dr." icinde gecer),
// projenin mevcut Python implementasyonundaki sirayla birebir ayni tutuldu.
const TITLE_EXPANSIONS: [string, string][] = [
    ['Uzm.Dr.', 'Uzman Doktor'],
    ['Prof.Dr.', 'Profesör Doktor'],
    ['Doç.Dr.', 'Doçent Doktor'],
    ['Op.Dr', 'Operatör Doktor'],
    ['Dr.Öğr.Üyesi', 'Doktora Öğretim Üyesi'],
    ['Dyt.', 'Diyetisyen'],
    ['Dr.', 'Doktor'],
    ['Pra. Dr.', 'Pratisyen Doktor'],
    ['Uzm. Klnk. Psk.', 'Uzman Klinik Psikolog'],
    ['Dt.', 'Diş Tabibi'],
    ['Uzm.Dt.', 'Uzman Diş Tabibi'],
];

const expandTitleAbbreviations = (text: string): string =>
    TITLE_EXPANSIONS.reduce((acc, [abbr, expansion]) => acc.split(abbr).join(expansion), text);

const buildSsml = (text: string, voiceName: string, rate: string) => `
<speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" xml:lang="tr-TR">
    <voice name="${voiceName}">
        <prosody rate="${rate}">${escapeSsml(text)}</prosody>
    </voice>
</speak>`;

export interface SpeakControl {
    // Oynatma dogal olarak bitince (veya hata alinca) resolve/reject olur.
    promise: Promise<void>;
    // Disaridan (kullanici interrupt ettiginde) oynatmayi aninda kesmek icin.
    stop: () => void;
}

export const speakWithAzure = (rawText: string, rate: string = SPEECH_RATE): SpeakControl => {
    const text = expandTitleAbbreviations(rawText);
    let settled = false;
    let player: sdk.SpeakerAudioDestination | null = null;
    let synthesizer: sdk.SpeechSynthesizer | null = null;
    let resolvePromise: () => void = () => {};

    const promise = new Promise<void>((resolve, reject) => {
        resolvePromise = resolve;

        const speechKey = process.env.REACT_APP_SPEECH_KEY;

        if (!speechKey) {
            reject(new Error('REACT_APP_SPEECH_KEY tanimli degil (.env.local dosyasini kontrol edin).'));
            return;
        }

        if (!text.trim()) {
            resolve();
            return;
        }

        const speechConfig = sdk.SpeechConfig.fromEndpoint(new URL(SPEECH_ENDPOINT), speechKey);
        speechConfig.speechSynthesisVoiceName = SPEECH_VOICE_NAME;
        speechConfig.speechSynthesisOutputFormat = sdk.SpeechSynthesisOutputFormat.Audio24Khz160KBitRateMonoMp3;

        player = new sdk.SpeakerAudioDestination();
        player.onAudioStart = () => console.log('[AzureTTS] oynatma baslatildi (play() cagrildi)');
        // Onemli: Promise burada resolve edilmeli, cunku speakTextAsync'in basari
        // callback'i ses SENTEZ edilir edilmez tetikleniyor, hoparlorden CALINMASI
        // bitince degil. Bu ikisini karistirmak, TTS sesi hala calarken mikrofonun
        // tekrar dinlemeye baslamasina ve kendi sesini algilamasina (yankı) yol aciyordu.
        player.onAudioEnd = () => {
            if (settled) return;
            settled = true;
            console.log('[AzureTTS] oynatma tamamlandi');
            resolve();
        };

        const audioConfig = sdk.AudioConfig.fromSpeakerOutput(player);
        synthesizer = new sdk.SpeechSynthesizer(speechConfig, audioConfig);

        console.log('[AzureTTS] sentez baslatiliyor, metin uzunlugu:', text.length, 'hiz:', rate);

        synthesizer.speakSsmlAsync(
            buildSsml(text, SPEECH_VOICE_NAME, rate),
            (result) => {
                synthesizer?.close();
                if (result.reason === sdk.ResultReason.SynthesizingAudioCompleted) {
                    console.log('[AzureTTS] sentez tamamlandi, ses boyutu (byte):', result.audioData?.byteLength);
                    // resolve buradan degil, player.onAudioEnd icinden yapiliyor
                } else if (!settled) {
                    settled = true;
                    console.error('[AzureTTS] sentez basarisiz. reason:', result.reason, 'detay:', result.errorDetails);
                    reject(new Error(result.errorDetails || 'Sesli okuma basarisiz oldu.'));
                }
            },
            (error) => {
                synthesizer?.close();
                if (!settled) {
                    settled = true;
                    console.error('[AzureTTS] hata:', error);
                    reject(new Error(String(error)));
                }
            }
        );
    });

    const stop = () => {
        if (settled) return;
        settled = true;
        console.log('[AzureTTS] interrupt: oynatma kesiliyor');
        try { player?.pause(); } catch { /* yoksayilir */ }
        try { player?.close(); } catch { /* yoksayilir */ }
        try { synthesizer?.close(); } catch { /* yoksayilir */ }
        resolvePromise();
    };

    return { promise, stop };
};
